import { captureException } from '@sentry/nuxt';
import useAuth from '~/composables/useAuth';
import type { FooterLink } from '../types';
import type {
  GetFooterPopularProductsQuery,
  GetFooterPopularProductsQueryVariables,
} from '~/apollo/types/types';
import { GetFooterPopularProducts } from '~/apollo/types/types';
import useWebinterfaceUrl from '~/composables/useWebinterfaceUrl';
import { getLegacyLocalePrefix } from '~/helpers/legacyLocale';

export const useFooterLinks = () => {
  const authStore = useAuthStore();
  const currencyStore = useCurrencyStore();
  const currentCurrency = computed<string>(() => currencyStore.currency);
  const { locale, t } = useI18n();
  const { login } = useAuth();
  const { region } = useRegion();

  const { refresh, data: popularGamesLinks } = useAsyncData<Array<FooterLink>>(
    `gp-footer-popular-products-${locale.value}`,
    async () => {
      try {
        const { data: response } = await useLegacyApollo<
          GetFooterPopularProductsQuery,
          GetFooterPopularProductsQueryVariables
        >(GetFooterPopularProducts, {
          locale: locale.value,
        });

        if (response.products != null && response.products.length > 0) {
          return response.products.map((game) => ({
            key: game.slug,
            type: 'nuxt',
            translate: false,
            label: game.name,
            data: {
              url: `/${locale.value}/${game.slug}`,
            },
          }));
        }
      } catch (error) {
        captureException(error);
      }

      return [];
    },
  );

  watch(locale, async () => {
    await refresh();
  });

  const gameCloudDashboardUrl = useWebinterfaceUrl(`/gamecloud`);

  const revokeOrderLink = computed<FooterLink>(() => ({
    key: 'revoke-order',
    translate: true,
    type: 'link',
    label: 'general/menu/item/revoke_order',
    data: {
      url: `mailto:support@g-portal.com?subject=${encodeURIComponent(t('general/menu/item/revoke_order_subject'))}&body=${encodeURIComponent(t('general/menu/item/revoke_order_body'))}`,
    },
  }));

  const profileLinks = computed<Array<FooterLink>>(() =>
    authStore.isAuthenticated
      ? /* LOGGED IN */ [
          {
            key: `${region.value}__my-servers`,
            translate: true,
            type: 'link',
            label: 'general/menu/item/my_servers',
            data: {
              url: gameCloudDashboardUrl.value,
            },
          },
          {
            key: `${region.value}__settings`,
            translate: true,
            type: 'nuxt',
            label: 'general/menu/item/your_settings',
            data: {
              url: `/${locale.value}/profile`,
            },
          },
          {
            key: `${region.value}__payments`,
            translate: true,
            type: 'nuxt',
            label: 'account/payments/page/title',
            data: {
              url: `/${locale.value}/account/payments`,
            },
          },
          revokeOrderLink.value,
        ]
      : /* NOT LOGGED IN */ [
          {
            key: 'login',
            translate: true,
            type: 'native',
            label: 'general/non_special_text/login',
            data: {
              onclick: login,
            },
          },
          {
            key: 'register',
            translate: true,
            type: 'link',
            label: 'frontend/register',
            data: {
              url: `/${locale.value}/register`,
            },
          },
          revokeOrderLink.value,
        ],
  );

  const gportalLinks = computed<Array<FooterLink>>(() => [
    {
      key: 'game-studios',
      translate: true,
      type: 'external',
      label: 'general/menu/item/game_studios',
      data: {
        url: `https://www.g-portal.com/business/`,
      },
    },
    {
      key: 'partner',
      translate: true,
      type: 'nuxt',
      label: 'general/menu/item/partner',
      data: {
        url: `/${locale.value}/partner`,
      },
    },
    {
      key: `sustainability`,
      label: 'general/menu/item/sustainability',
      translate: true,
      type: 'nuxt',
      data: {
        url: `/${locale.value}/sustainability`,
      },
    },
    {
      key: 'status',
      translate: true,
      type: 'external',
      label: 'frontend/status',
      data: {
        url: `https://status.g-portal.com`,
      },
    },
    {
      key: 'jobs',
      translate: true,
      type: 'external',
      label: 'frontend/jobs',
      data: {
        url: `https://www.g-portal.com/business/jobs`,
      },
    },
    {
      key: 'wiki',
      translate: true,
      type: 'external',
      label: 'general/menu/item/wiki',
      data: {
        url: `/wiki/${locale.value}`,
      },
    },
    {
      key: 'terms',
      translate: true,
      type: 'external',
      label: 'meta/terms',
      data: {
        url: `/${getLegacyLocalePrefix(
          locale.value,
          currentCurrency.value,
        )}/terms`,
      },
    },
    {
      key: 'privacy',
      translate: true,
      type: 'external',
      label: 'meta/privacy',
      data: {
        url: `/${getLegacyLocalePrefix(
          locale.value,
          currentCurrency.value,
        )}/privacy`,
      },
    },
    {
      key: 'imprint',
      translate: true,
      type: 'external',
      label: 'meta/imprint',
      data: {
        url: `/${getLegacyLocalePrefix(
          locale.value,
          currentCurrency.value,
        )}/imprint`,
      },
    },
  ]);

  return {
    profileLinks,
    gportalLinks,
    popularGamesLinks,
    refresh,
  };
};
